import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Portal } from './Portal';
import { Icon } from './Sidebar';
import { placeMenu, type Anchor } from '../lib/placeMenu';

/**
 * The gear on a page header.
 *
 * Page headers had grown into a shelf of everything a page could do — copy
 * this week, clear that one, two checkboxes and a line of instructions — sat
 * above the thing the teacher actually came to look at. Most of it is used
 * twice a term. This puts that behind one small button, so the header is a
 * title and the two or three things worth pressing daily.
 *
 * Portalled and positioned from the button's own rectangle. Headers sit
 * inside cards and panels that clip and stack, and a menu rendered in place
 * ends up underneath the page it belongs to.
 */
export function HeaderMenu({
  label = 'Page settings',
  children,
  width = 260,
  glyph,
  className,
}: {
  label?: string;
  children: ReactNode | ((close: () => void) => ReactNode);
  width?: number;
  /**
   * What the button shows. The gear by default.
   *
   * A menu hung off a single to-do item wants an arrow rather than a gear —
   * same behaviour, same escaping, different picture. Passing it in is what
   * keeps there being one popover in the app rather than two that drift.
   */
  glyph?: ReactNode;
  className?: string;
}) {
  /** The button's rectangle when it was opened, in viewport coordinates. */
  const [at, setAt] = useState<Anchor | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  /**
   * How tall the panel wants to be.
   *
   * Measured after it has rendered rather than guessed, because whether it
   * fits below the button is the whole question and the answer depends on how
   * many options this particular menu has.
   */
  const [wanted, setWanted] = useState(Infinity);

  const close = () => setAt(null);

  const open = () => {
    const box = button.current!.getBoundingClientRect();
    setWanted(Infinity);
    setAt({ top: box.top, bottom: box.bottom, right: box.right });
  };

  useEffect(() => {
    if (!at || !panel.current) return;
    setWanted(panel.current.scrollHeight);
  }, [at]);

  /*
   * Follow the button if the window changes shape underneath it. Rotating a
   * tablet with a menu open used to leave the panel pointing at where the
   * button had been.
   */
  useEffect(() => {
    if (!at) return;

    function reposition() {
      if (!button.current) return;
      const box = button.current.getBoundingClientRect();
      setAt({ top: box.top, bottom: box.bottom, right: box.right });
    }

    window.addEventListener('resize', reposition);
    return () => window.removeEventListener('resize', reposition);
  }, [at]);

  // Escape closes it, wherever the focus happens to be.
  useEffect(() => {
    if (!at) return;

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close();
    }

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [at]);

  return (
    <>
      <button
        ref={button}
        onClick={() => (at ? close() : open())}
        aria-expanded={!!at}
        aria-label={label}
        title={label}
        className={
          className ??
          'rounded-lg p-1.5 text-clay transition-colors hover:bg-linen hover:text-terracotta'
        }
      >
        {glyph ?? <Icon name="settings" size={17} />}
      </button>

      {at && (
        <Portal>
          <div className="fixed inset-0 z-[85]" onClick={close}>
            <div
              ref={panel}
              role="dialog"
              aria-label={label}
              onClick={(e) => e.stopPropagation()}
              style={{
                width,
                /*
                 * Placed rather than assumed to fit. A menu on a row near the
                 * bottom of the screen used to open downwards regardless and
                 * run off the edge — the options past the fold could not be
                 * reached or scrolled to, because the panel had not run out of
                 * room, the window had.
                 */
                ...(() => {
                  const box = placeMenu(
                    at,
                    { width: window.innerWidth, height: window.innerHeight },
                    width,
                    wanted,
                  );
                  return { left: box.left, top: box.top, maxHeight: box.maxHeight };
                })(),
              }}
              className="absolute overflow-y-auto rounded-card border border-sand bg-cream p-3 shadow-lifted"
            >
              {typeof children === 'function' ? children(close) : children}
            </div>
          </div>
        </Portal>
      )}
    </>
  );
}

/** A labelled group inside the menu. */
export function MenuSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mb-3 last:mb-0">
      <p className="mb-1.5 text-[11px] uppercase tracking-wide text-stone">{title}</p>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

/** A checkbox row, sized for a menu rather than a form. */
export function MenuToggle({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  children: ReactNode;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 rounded-lg px-1 py-1 text-sm text-walnut transition-colors hover:bg-linen">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-3.5 w-3.5 shrink-0 accent-terracotta"
      />
      {children}
    </label>
  );
}
